import React from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  SOURCE_LEGEND,
  type SourceLegendKey
} from "../fieldSource/sourceKinds";

/** The 3px tone stripe a filled field carries, as the legend draws it. */
export function SourceStripeSwatch({ className }: { className: string }) {
  return (
    <span
      aria-hidden
      className={cn("h-3 w-0.75 shrink-0 rounded-xs", className)}
    />
  );
}

/**
 * The page legend in the viewer bar, with a count per kind. Clicking a kind
 * shows only its stripes; clicking it again shows them all.
 */
export function SourcesLegend({
  counts,
  isolated,
  onIsolate
}: {
  counts: Partial<Record<SourceLegendKey, number>>;
  isolated: SourceLegendKey | null;
  onIsolate: (key: SourceLegendKey | null) => void;
}) {
  const entries = SOURCE_LEGEND.filter((spec) => (counts[spec.key] ?? 0) > 0);
  if (!entries.length) return null;
  return (
    <div
      role="group"
      aria-label="Source Kinds"
      className="flex min-w-0 items-center gap-0.5"
    >
      {entries.map((spec) => {
        const on = isolated === spec.key;
        return (
          <Button
            key={spec.key}
            type="button"
            variant="ghost"
            size="xs"
            aria-pressed={on}
            title={on ? "Show All Sources" : `Only ${spec.label}`}
            onClick={() => onIsolate(on ? null : spec.key)}
            className={cn(
              "gap-1.5 font-normal text-fg-2",
              on && "bg-fill-2 text-fg",
              isolated && !on && "opacity-50"
            )}
          >
            <SourceStripeSwatch className={spec.stripe} />
            {spec.label}
            <span className="text-fg-4 tabular-nums">{counts[spec.key]}</span>
          </Button>
        );
      })}
    </div>
  );
}
